<template>
  <div class="ma-4 px-4">
    <v-row dense align="center">
      <v-col cols="12" sm="6" md="2">
        <v-autocomplete
          v-model="filters.program_id"
          :items="programs"
          :item-text="programText"
          item-value="id"
          label="Programme"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-autocomplete
          v-model="filters.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-select
          v-model="filters.year_batch"
          :items="batches"
          label="Batch"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-select
          v-model="filters.term_no"
          :items="termOptions"
          label="Term No"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="4" md="2" class="d-flex align-center">
        <v-btn class="primary white--text mr-2" @click="applySearch">
          Search
        </v-btn>
        <v-btn
          class="primary white--text"
          :loading="downloadLoading"
          :disabled="downloadLoading"
          @click="downloadAll"
        >
          <v-icon left small>mdi-download</v-icon>
          Download
        </v-btn>
      </v-col>
    </v-row>

    <v-tabs v-model="tab" class="mt-2 mb-2" background-color="transparent">
      <v-tab href="#pending">
        Pending
        <v-chip x-small class="ml-2" color="orange" text-color="white">
          {{ pendingItems.length }}
        </v-chip>
      </v-tab>
      <v-tab href="#published">
        Published
        <v-chip x-small class="ml-2" color="success" text-color="white">
          {{ publishedItems.length }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="tableItems"
            :loading="loading"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.batch`]="{ item }">
              {{ item.year }}<span v-if="item.batch"> - {{ item.batch }}</span>
            </template>

            <template #[`item.total_students`]="{ item }">
              <v-chip
                small
                :color="item.all_marked ? 'success' : 'grey lighten-2'"
                :text-color="item.all_marked ? 'white' : 'black'"
              >
                <v-icon x-small left>mdi-account-group</v-icon>
                {{ item.marked_students }} / {{ item.total_students }}
              </v-chip>
            </template>

            <template #[`item.from_date`]="{ item }">
              {{ item.from_date ? formatDate(item.from_date) : "-" }}
            </template>

            <template #[`item.to_date`]="{ item }">
              {{ item.to_date ? formatDate(item.to_date) : "-" }}
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip
                v-if="item.published"
                small
                color="success"
                text-color="white"
              >
                <v-icon x-small left>mdi-check-decagram</v-icon> Published
              </v-chip>
              <v-chip
                v-else
                small
                :color="item.all_marked ? 'primary' : 'orange'"
                outlined
              >
                Pending
              </v-chip>
            </template>

            <template #[`item.student_electives`]="{ item }">
              <v-icon color="accent" @click="navigateToStudentElectives(item)">
                mdi-dots-horizontal
              </v-icon>
            </template>

            <template #[`item.action`]="{ item }">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-if="!item.published"
                    small
                    color="accent"
                    class="mr-2"
                    v-bind="attrs"
                    v-on="on"
                    @click="openEditDialog(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>Edit due date</span>
              </v-tooltip>

              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    small
                    color="accent"
                    class="mr-2"
                    :disabled="downloadingKey === rowKey(item)"
                    v-bind="attrs"
                    v-on="on"
                    @click="downloadConfig(item)"
                  >
                    mdi-download
                  </v-icon>
                </template>
                <span>Download this config</span>
              </v-tooltip>

              <v-tooltip v-if="!item.published" top>
                <template #activator="{ on, attrs }">
                  <div class="d-inline-block" v-bind="attrs" v-on="on">
                    <v-icon
                      small
                      color="success"
                      :disabled="
                        publishingKey === rowKey(item) ||
                        (item.is_elective && !item.all_marked)
                      "
                      @click="publishConfig(item)"
                    >
                      mdi-publish
                    </v-icon>
                  </div>
                </template>
                <span>{{ publishTooltip(item) }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog" max-width="420" persistent>
      <v-card v-if="editItem">
        <v-card-title class="font-weight-medium secondary--text primary">
          Edit Due Date
          <v-spacer />
          <v-btn small icon @click="editDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5">
          <v-menu
            v-model="editFromDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(editFromDate)"
                label="Start Date"
                outlined
                dense
                hide-details="auto"
                readonly
                class="mb-4"
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              />
            </template>
            <v-date-picker
              v-model="editFromDate"
              @input="editFromDatePicker = false"
            />
          </v-menu>

          <v-menu
            v-model="editToDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(editToDate)"
                label="End Date"
                outlined
                dense
                hide-details="auto"
                readonly
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              />
            </template>
            <v-date-picker
              v-model="editToDate"
              @input="editToDatePicker = false"
            />
          </v-menu>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn
            class="primary white--text"
            :loading="saving"
            @click="saveEdit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { electiveConfigurationHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      items: [],
      programs: [],
      partners: [],
      batches: [],
      loading: false,
      headers: electiveConfigurationHeaders,
      tab: "pending",
      filters: {
        program_id: null,
        partner_id: null,
        year_batch: null,
        term_no: null,
      },
      appliedFilters: {
        program_id: null,
        partner_id: null,
        year_batch: null,
        term_no: null,
      },
      downloadLoading: false,
      downloadingKey: null,
      publishingKey: null,
      editDialog: false,
      editItem: null,
      editFromDate: null,
      editToDate: null,
      editFromDatePicker: false,
      editToDatePicker: false,
      saving: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    termOptions() {
      return [
        ...new Set(this.items.map((i) => i.term_no).filter((v) => v != null)),
      ]
        .sort((a, b) => a - b)
        .map((v) => ({ text: `Term ${v}`, value: v }));
    },
    filteredItems() {
      return this.items
        .filter((item) => {
          const f = this.appliedFilters;
          return (
            (f.program_id == null || item.program_id === f.program_id) &&
            (f.partner_id == null || item.partner_id === f.partner_id) &&
            (f.year_batch == null ||
              `${item.year}-${item.batch}` === f.year_batch) &&
            (f.term_no == null || item.term_no === f.term_no)
          );
        })
        .sort((a, b) => {
          // Current to old: newest year first, then newest batch, then term.
          const yearDiff = (b.year ?? 0) - (a.year ?? 0);
          if (yearDiff !== 0) return yearDiff;
          const batchDiff = (b.batch ?? 0) - (a.batch ?? 0);
          if (batchDiff !== 0) return batchDiff;
          return (a.term_no ?? 0) - (b.term_no ?? 0);
        });
    },
    pendingItems() {
      return this.filteredItems.filter((i) => !i.published);
    },
    publishedItems() {
      return this.filteredItems.filter((i) => i.published);
    },
    tableItems() {
      return this.tab === "published" ? this.publishedItems : this.pendingItems;
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Elective Configuration Old" }]);
    const restored = this.readFiltersFromQuery();
    this.filters = { ...restored };
    this.appliedFilters = { ...restored };
    this.fetchStudentElectiveConfigs();
    this.fetchPrograms();
    this.fetchPartners();
    this.fetchBatches();
  },
  methods: {
    readFiltersFromQuery() {
      const q = this.$route.query;
      return {
        program_id: q.program_id != null ? Number(q.program_id) : null,
        partner_id: q.partner_id != null ? Number(q.partner_id) : null,
        year_batch: q.year_batch || null,
        term_no: q.term_no != null ? Number(q.term_no) : null,
      };
    },

    programText(item) {
      const shortName = item.degree?.short_name || "";
      return item.specialization
        ? `${shortName} - ${item.specialization}`
        : shortName || String(item.id);
    },

    async fetchPrograms() {
      try {
        const endpoint =
          this.currentUser && this.currentUser.partner_id != 1
            ? `/get_partner_programs/${this.currentUser.partner_id}`
            : "/programs";
        const res = await this.$axios.get(endpoint);
        this.programs = res.data.data || [];
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to fetch programs",
          color: "error",
        });
      }
    },

    async fetchPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data || [];
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to fetch partners",
          color: "error",
        });
      }
    },

    async fetchBatches() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data || [];
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to fetch batches",
          color: "error",
        });
      }
    },

    rowKey(item) {
      return `${item.batch_scheme_id}-${item.term_no}`;
    },

    async fetchStudentElectiveConfigs() {
      this.loading = true;
      try {
        const res = await this.$axios.get("/list_elective_configs_for_admin");
        this.items = res.data.data;
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to fetch",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    applySearch() {
      this.appliedFilters = { ...this.filters };
      const query = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value != null && value !== "") query[key] = value;
      });
      this.$router.replace({ query }).catch(() => {});
    },

    clearFilters() {
      this.filters = {
        program_id: null,
        partner_id: null,
        year_batch: null,
        term_no: null,
      };
    },

    activeFilterParams() {
      const params = {};
      Object.entries(this.appliedFilters).forEach(([key, value]) => {
        if (value == null || value === "") return;
        if (key === "year_batch") {
          const [year, batch] = String(value).split("-");
          params.year = year;
          params.batch = batch;
        } else {
          params[key] = value;
        }
      });
      return params;
    },

    triggerCsvDownload(blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    // Parse CSV text into a 2D array (handles quoted fields, escaped quotes)
    parseCsv(text) {
      const rows = [];
      let row = [];
      let field = "";
      let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (inQuotes) {
          if (char === '"') {
            if (text[i + 1] === '"') {
              field += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            field += char;
          }
        } else if (char === '"') {
          inQuotes = true;
        } else if (char === ",") {
          row.push(field);
          field = "";
        } else if (char === "\n") {
          row.push(field);
          rows.push(row);
          row = [];
          field = "";
        } else if (char !== "\r") {
          field += char;
        }
      }
      if (field !== "" || row.length) {
        row.push(field);
        rows.push(row);
      }
      return rows;
    },

    serializeCsv(rows) {
      return rows
        .map((row) =>
          row
            .map((cell) => {
              const value = cell == null ? "" : String(cell);
              return /[",\n\r]/.test(value)
                ? `"${value.replace(/"/g, '""')}"`
                : value;
            })
            .join(","),
        )
        .join("\r\n");
    },

    // Remove columns whose header matches the predicate from a CSV blob.
    async stripCsvColumns(blob, matchHeader) {
      const text = await new Blob([blob]).text();
      const rows = this.parseCsv(text);
      if (!rows.length) return blob;
      const dropIndexes = rows[0].reduce((acc, header, idx) => {
        if (matchHeader(header)) acc.push(idx);
        return acc;
      }, []);
      if (!dropIndexes.length) return blob;
      const dropSet = new Set(dropIndexes);
      const filtered = rows.map((row) =>
        row.filter((_, idx) => !dropSet.has(idx)),
      );
      return new Blob([this.serializeCsv(filtered)], { type: "text/csv" });
    },

    isAvailableElectivesHeader(header) {
      const normalized = String(header || "").toLowerCase();
      return normalized.includes("available") && normalized.includes("elective");
    },

    async downloadAll() {
      this.downloadLoading = true;
      try {
        const published = this.tab === "published";
        const res = await this.$axios.get(
          "/student_elective_configs/download",
          {
            params: { ...this.activeFilterParams(), published },
            responseType: "blob",
          },
        );
        const filename = published
          ? "published_elective_configs.csv"
          : "pending_elective_configs.csv";
        const blob = published
          ? await this.stripCsvColumns(
              res.data,
              this.isAvailableElectivesHeader,
            )
          : res.data;
        this.triggerCsvDownload(blob, filename);
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to download",
          color: "error",
        });
      } finally {
        this.downloadLoading = false;
      }
    },

    async downloadConfig(item) {
      this.downloadingKey = this.rowKey(item);
      try {
        const res = await this.$axios.get(
          "/student_elective_configs/download",
          {
            params: {
              program_batch_id: item.program_batch_id,
              partner_id: item.partner_id,
              term_no: item.term_no,
              batch_scheme_id: item.batch_scheme_id,
            },
            responseType: "blob",
          },
        );
        const name = `${item.program_name || "electives"}_term_${
          item.term_no
        }.csv`.replace(/[^\w.-]+/g, "_");
        const blob = item.published
          ? await this.stripCsvColumns(
              res.data,
              this.isAvailableElectivesHeader,
            )
          : res.data;
        this.triggerCsvDownload(blob, name);
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to download",
          color: "error",
        });
      } finally {
        this.downloadingKey = null;
      }
    },

    publishTooltip(item) {
      if (item.is_elective && !item.all_marked)
        return "All students must select electives first";
      return "Publish";
    },

    async publishConfig(item) {
      if (item.published) return;
      if (item.is_elective && !item.all_marked) return;
      this.publishingKey = this.rowKey(item);
      try {
        const res = await this.$axios.put(
          "/student_elective_configs/set_published",
          {
            student_elective_config: {
              batch_scheme_id: item.batch_scheme_id,
              program_scheme_id: item.program_scheme_id,
              partner_id: item.partner_id,
              term_no: item.term_no,
              program_batch_id: item.program_batch_id,
              published: true,
            },
          },
        );

        const updated = res.data.data || res.data;
        item.published = updated.published ?? true;
        item.config_id = updated.id ?? item.config_id;

        this.$store.commit("showSnackbar", {
          message: "Config published",
          color: "success",
        });
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.errors?.published?.[0] ||
            error.response?.data?.message ||
            "Failed to update status",
          color: "error",
        });
      } finally {
        this.publishingKey = null;
      }
    },

    openEditDialog(item) {
      this.editItem = item;
      this.editFromDate = item.from_date;
      this.editToDate = item.to_date;
      this.editDialog = true;
    },

    async saveEdit() {
      if (!this.editItem) return;

      this.saving = true;
      try {
        const res = await this.$axios.put("/student_elective_configs/upsert", {
          student_elective_config: {
            batch_scheme_id: this.editItem.batch_scheme_id,
            program_scheme_id: this.editItem.program_scheme_id,
            partner_id: this.editItem.partner_id,
            term_no: this.editItem.term_no,
            from_date: this.editFromDate,
            to_date: this.editToDate,
          },
        });

        const updated = res.data.data || res.data;

        const idx = this.items.findIndex(
          (i) =>
            i.batch_scheme_id === this.editItem.batch_scheme_id &&
            i.term_no === this.editItem.term_no,
        );
        if (idx !== -1) {
          this.items.splice(idx, 1, {
            ...this.items[idx],
            config_id: updated.id ?? this.items[idx].config_id,
            from_date: updated.from_date ?? this.editFromDate,
            to_date: updated.to_date ?? this.editToDate,
          });
        }

        this.$store.commit("showSnackbar", {
          message: "Elective config updated",
          color: "success",
        });
        this.editDialog = false;
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to update",
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const [y, m, d] = String(dateStr).split("T")[0].split("-");
      if (!y || !m || !d) return dateStr;
      return `${d}-${m}-${y}`;
    },

    navigateToStudentElectives(item) {
      this.$router.push({
        path: "/student_electives",
        query: {
          program_batch_id: item.program_batch_id,
          partner_id: item.partner_id,
          term_no: item.term_no,
          batch_scheme_id: item.batch_scheme_id,
        },
      });
    },
  },
};
</script>

<style></style>
